import { ComponentProps, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { type BodyText } from '@garnish/core';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { type OrderingLedger } from '../../machine';
import { prepareLedger } from '../../utils';

/**
 * Extracts the ledger items from the ledger.
 */
export const useLedgerItems = (params: UseLedgerItemsParams): readonly LedgerItem[] => {
  const {
    ledger,
    tip,
    maxTipAmount,
    availableCredit,
    shouldUseCredit,
    amountEligibleForFlex,
    shouldUseFlex,
    discountInfo,
  } = params;

  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');
  const { formatMessage, formatNumber } = useIntl();

  return useMemo(() => {
    const formatPrice = (value: string | number) =>
      formatNumber(Number(value) / 100, {
        style: 'currency',
        currency: 'USD',
      });

    const { total, totalWithoutCredit, credit } = prepareLedger({
      ledger,
      tip,
      availableCredit,
      shouldUseCredit,
      amountEligibleForFlex,
      shouldUseFlex,
    });

    const subtotalItem: LedgerItem = {
      amount: formatPrice(ledger?.subtotal ?? 0),
      label: formatMessage(messages.subTotal),
    };

    const taxesItem: LedgerItem = {
      amount: `${formatPrice(ledger?.tax ?? 0)}`,
      label: formatMessage(messages.tax),
    };

    const feesItems =
      ledger?.fees
        ?.filter((fee) => fee.amount)
        .map((fee) => ({
          label: fee.name,
          amount: formatPrice(fee.amount ?? 0),
          helperText: fee.description ?? undefined,
        })) ?? [];

    const tipItem: LedgerItem = {
      label: formatMessage(messages.tip),
      amount: formatPrice(tip),
      isInvalid: tip > maxTipAmount,
      helperText: undefined,
    };

    const creditsItems =
      shouldUseCredit && credit > 0
        ? [
            {
              label: formatMessage(messages.credit),
              amount: `-${formatPrice(credit)}`,
              palette: 'success',
            },
          ]
        : [];

    const discountsItems =
      ledger?.discounts
        ?.filter((discount) => discount.amount)
        .map((discount) => ({
          label: discount.name,
          amount: `-${formatPrice(discount.amount ?? 0)}`,
          helperText: discountInfo ?? discount.description,
          palette: 'success',
        })) ?? [];

    const totalItem: LedgerItem = {
      label: formatMessage(messages.total),
      amount: `${formatPrice(total ?? 0)}`,
      isInvalid: tip > maxTipAmount,
      bold: true,
      sizeMatch: ['16'],
      testID: 'ledger.total',
    };

    const totalWithoutCreditItem: LedgerItem = {
      label: formatMessage(messages.total),
      amount: `${formatPrice(totalWithoutCredit ?? 0)}`,
      bold: true,
      sizeMatch: ['16'],
      testID: 'ledger.total',
    };

    return [
      subtotalItem,
      taxesItem,
      ...feesItems,
      ...(tip ? [tipItem] : []),
      ...discountsItems,

      // Credits are not shown on the ledger when flex is enabled.
      ...(isFlexEnabled ? [] : creditsItems),
      ...(isFlexEnabled ? [totalWithoutCreditItem] : [totalItem]),
    ].filter(Boolean) as readonly LedgerItem[];
  }, [
    isFlexEnabled,
    ledger,
    tip,
    maxTipAmount,
    availableCredit,
    shouldUseCredit,
    amountEligibleForFlex,
    shouldUseFlex,
    discountInfo,
    formatMessage,
    formatNumber,
  ]);
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  subTotal: {
    defaultMessage: 'Subtotal',
    description: 'Bag | Ledger | Subtotal',
  },
  tip: {
    defaultMessage: 'Tip',
    description: 'Bag | Ledger | Tip',
  },
  tax: {
    defaultMessage: 'Tax',
    description: 'Bag | Ledger | Tax',
  },
  credit: {
    defaultMessage: 'Credit',
    description: 'Bag | Ledger | Credit',
  },
  total: {
    defaultMessage: 'Total',
    description: 'Bag | Ledger | Total',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type UseLedgerItemsParams = {
  ledger?: OrderingLedger;
  tip: number;
  maxTipAmount: number;
  availableCredit: number;
  shouldUseCredit: boolean;
  amountEligibleForFlex: number | null;
  shouldUseFlex: boolean;
  discountInfo?: string; // information shown on a discount tooltip.
};

type LedgerItem = Readonly<{
  label: string;

  /**
   * Amount can be represented as a simple string
   * or a collection of strings joined by " + " sign.
   */
  amount: string | readonly string[];

  sizeMatch?: ComponentProps<typeof BodyText>['sizeMatch'];
  bold?: boolean;
  palette?: 'standard' | 'success';

  isInvalid?: boolean;

  /**
   * Optional helper text to be shown in tooltip/modal.
   */
  helperText?: string;
  testID?: string;
}>;
